import { useQuery, gql } from '@apollo/client';
import {
  ContentDeciderContainer,
  Loader,
  transformViewListData,
  Generator,
  withErrorBoundary
} from '@nile/components';
import {
  RMMClient,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import TicketContext from '@nile/mangifera/src/Contexts/TicketContext';
import TableView from '@nile/banyan/src/Pages/ServiceCatalog/TableView';
import { isEditable } from '@nile/mangifera/src/Pebbles/Favourites';
import {
  columnsConfig as assetColumnConfig,
  getColumnAccessor,
  getColumnCellType,
  getFieldsToBeRequested,
  getAssetClassVariables
} from '@nile/rmm/src/modules/asset-management/services/asset-list-helper';
import { assetDetailsTab } from '@nile/rmm/src/modules/alert-management/constants/constants';
import { getTabTemplate } from '@nile/mangifera/src/Pebbles/TabManager';
import { cloneDeep, isEmpty } from 'lodash';
import { getProcessedConfig } from '@nile/rmm/src/modules/common/services/tableHelper';
import React, { useCallback, useContext, useState } from 'react';
import sizeMe from 'react-sizeme';
import { getEmoji } from '@nile/mangifera/src/Pebbles/WidgetEmoji';
import { Tag } from '@superops/ui-design-system';
import { getAssetViewsConfig } from '@nile/rmm/src/modules/home/components/sub-pages/sidebar/sidebar-helper';
import { SVGIcon } from '@superops/super-icons';
import {
  WidgetWrapper,
  Header,
  Title,
  Content,
  Actions,
  NumberCardContainer,
  NumberTitle,
  Badge
} from '../HomeWidgetContainer';
import DeletedView from './DeletedView';
import AccessDenied from './AccessDenied';

function AssetViewWidget(props) {
  const {
    widget,
    user,
    meta,
    size,
    intl,
    assetViews,
    deleteWidget,
    assetViewMeta
  } = props;

  const viewPermission = widget?.permission;
  const viewExist = assetViews.some((el) => el.id === widget.id);

  const context = useContext(HomeContext);
  const tabContext = useContext(TicketContext);
  const [loading, setLoading] = useState(!!widget && viewPermission);
  const [data, setData] = useState([]);
  const [error, setError] = useState({});
  const [listInfo, setListInfo] = useState({});
  const [columnGroup, setColumnGroup] = useState([
    { Header: 'Name', columns: [] }
  ]);

  const { configuredCols } = getProcessedConfig({
    entitiesMeta: meta,
    allColumns: assetViewMeta?.selectAttributes,
    intl,
    module: 'WorkStation',
    columnPersonalization: [],
    searchAttributes: assetViewMeta?.searchAttributes
  });
  const columns = configuredCols;

  let currentView =
    viewPermission && viewExist
      ? assetViews.find((el) => el.id === widget?.id)
      : widget;

  currentView = {
    ...currentView,
    module: 'ASSET_VIEWS',
    iconColor: 'black'
  };

  const options = {
    pageSize: 20,
    page: 1,
    view: { id: widget?.id },
    sort: [
      {
        attribute: 'name',
        order: 'ASC'
      }
    ]
  };

  const navigateToView = () => {
    if (!viewPermission || !viewExist) return;
    const currentAppState = context?.userAppState || { appState: [] };
    const updatedAppState = cloneDeep(currentAppState?.appState);
    const tabIdx = updatedAppState.findIndex((el) => el.id === 'rmm-home');
    const tab = {
      id: 'rmm-home',
      content: getTabTemplate({ module: 'RMM_HOME' }),
      accessibilityLabel: 'RMM Home',
      module: 'RMM_HOME',
      tabId: 1,
      state: {},
      data: {},
      url: `/rmm/asset-view/${currentView.id}`
    };
    if (tabIdx === -1) {
      updatedAppState.push(tab);
    } else {
      updatedAppState[tabIdx] = tab;
    }
    context.updateUserAppState({
      ...currentAppState,
      appState: updatedAppState,
      activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
    });
    context.closeHomeOverlay();
    setTimeout(() => {
      context.switchTabFocus(tab);
    }, 200);
  };

  const navToDetailsPage = (obj) => {
    const { assetId, name, assetClass } = obj?.row?.original;
    tabContext.openTab({
      module: assetDetailsTab,
      data: {
        id: assetId,
        subject: name,
        classId: assetClass?.classId
      }
    });
  };

  const getActions = useCallback((col) => {
    switch (col.name) {
      case 'name':
        return {
          onClick: navToDetailsPage
        };
      default:
        return {};
    }
  });

  const getView = (config) => {
    if (config && config.list) {
      return config.list.find((item) => item.id === currentView.id);
    }
  };

  const getActiveView = (section) => {
    const config = getAssetViewsConfig();
    config.list = assetViews;
    const processedConfig = transformViewListData(config);
    switch (section) {
      case 'asset':
      case 'asset-view':
        return getView(processedConfig);
      default:
        return {};
    }
  };

  const fetchViewData = async (personalization, pageOptions) => {
    setLoading(true);
    const viewData = getActiveView('asset-view');

    const personalizedColumns = personalization
      ? JSON.parse(personalization)
      : columns;

    const { configuredCols } = getProcessedConfig({
      entitiesMeta: meta,
      allColumns: assetViewMeta?.selectAttributes,
      intl,
      module: 'WorkStation',
      columnPersonalization: personalizedColumns,
      defaultColumns: assetViewMeta?.defaultSelectedAttributes,
      searchAttributes: assetViewMeta?.searchAttributes
    });

    const generator = new Generator({
      operation: 'getWorkStationList',
      variables: getAssetClassVariables(pageOptions?.page || options?.page),
      fields: getFieldsToBeRequested(configuredCols)
    });

    const queryString = generator.queryBuilder();
    const assetQuery = gql`
      ${queryString.query}
    `;

    const response = await RMMClient.query({
      query: assetQuery,
      variables: {
        listInfo: pageOptions
          ? { ...pageOptions, view: { id: widget.id } }
          : options
      },
      fetchPolicy: 'network-only'
    });
    setLoading(false);
    const cols = personalizedColumns;
    const allColumns = cols.map((col) => {
      return {
        Header: col.label,
        accessor: getColumnAccessor(col),
        type: getColumnCellType(col, viewData),
        disableSortBy: true,
        disableFilters: true,
        disableResizing: false,
        className: 'sticky',
        editable: isEditable(col),
        actions: getActions(col),
        field: col,
        module: 'WorkStation'
      };
    });
    setColumnGroup([{ Header: 'Name', columns: allColumns }]);
    setData(response?.data?.getWorkStationList?.assets || []);
    if (response?.errors) {
      setError(response?.errors);
    }
    setListInfo(response?.data?.getWorkStationList?.listInfo);
  };

  useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: user.userId,
      category: 'AssetView',
      name: widget?.id
    },
    client: AccountClient,
    skip: !viewPermission,
    onCompleted(res) {
      setLoading(true);
      const personalizationJSON =
        res?.getPersonalizationByCategoryAndName?.value;
      fetchViewData(personalizationJSON);
    },
    onError(err) {
      setLoading(false);
      console.log(err);
    }
  });

  if (loading) {
    return (
      <WidgetWrapper>
        <div className="widget-header flex center fg1">
          <Actions className="widget-actions visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
        </div>
        <Loader />
      </WidgetWrapper>
    );
  }
  return (
    <WidgetWrapper>
      {size.width > 300 && size.height > 200 ? (
        <>
          <Header>
            <Title onClick={navigateToView}>
              <Badge className="mr15" color="#ae39b24d">
                {getEmoji(currentView)}
              </Badge>
              {currentView?.name}
              {viewPermission && viewExist && (
                <div className="ml-10">
                  <Tag>
                    {data?.length}/{listInfo?.totalCount}
                  </Tag>
                </div>
              )}
            </Title>
            <div className="widget-header flex center fg1">
              <Actions className="widget-actions visibilityActions">
                <div onClick={deleteWidget.bind(this, widget)} className="ptr">
                  <SVGIcon name="Delete" />
                </div>
              </Actions>
            </div>
          </Header>
          {!viewPermission ? (
            <AccessDenied />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView />
              ) : (
                <Content>
                  <ContentDeciderContainer
                    isLoading={loading}
                    hasContent={!loading && data?.length > 0}
                    fullHeight
                    mainContent={
                      <TableView
                        noBulkSelect
                        context={context}
                        columns={columnGroup}
                        data={data}
                        setData={setData}
                        rowCount={20}
                        refreshTable={() => {}}
                        defaultColumnCells={assetColumnConfig}
                        pagination={false}
                        pages={1}
                        activePage={0}
                        fullHeight
                      />
                    }
                    newEmptyContainer
                    error={!isEmpty(error)}
                    loadingContent={<Loader />}
                    notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
                  />
                </Content>
              )}
            </>
          )}
        </>
      ) : (
        <NumberCardContainer>
          <Badge color="#ae39b24d" className="icon">
            {getEmoji(currentView)}
          </Badge>
          <Actions className="icon visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
          {!viewPermission ? (
            <AccessDenied small />
          ) : (
            <>
              {!viewExist ? (
                <DeletedView small />
              ) : (
                <div className="content ptr" onClick={navigateToView}>
                  <NumberTitle>{listInfo?.totalCount}</NumberTitle>
                  {currentView?.name}
                </div>
              )}
            </>
          )}
        </NumberCardContainer>
      )}
    </WidgetWrapper>
  );
}

export default withErrorBoundary(
  sizeMe({ monitorHeight: true })(AssetViewWidget)
);
